import React, { useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, HStack, IconButton, Input, Switch, Table, Tbody, Td, Text,
  Th, Thead, Tr, useColorModeValue,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { Field, useForm, useFormState } from 'react-final-form';
import { useConfirm } from '../ConfirmProvider';

// item 954 (Chad, priced-externally epic) — the MANUAL pricing panel for deals whose economics
// were decided OUTSIDE the engine (e.g. sq-ft pricing). Toggling ON pauses the engine for this
// proposal (confirmed both ways — OFF re-runs the engine and WIPES these numbers) and every
// figure is hand-entered ("everything gets hand entered for safety"). The numbers land in
// rnwyManualPricingJson — the server maps them into the exact engine-result shape all
// downstream consumers read, so proposal = WO = invoice = contract, always.
interface ManualOption {
  config: number; graphics: number; dev: number;
  licenseOneTime: number; monthlyLicense: number; retainBuyIn: number;
}
interface ExtraLine { label: string; perpetual: number; twoYear: number; fiveYear: number }
interface ManualPricing {
  perpetual: ManualOption; twoYear: ManualOption; fiveYear: ManualOption;
  helpDesk: { gold: number; platinum: number; titanium: number };
  switchPilot: { offered: boolean; oneTimeConfig: number; monthly: number };
  // item 959 — arbitrary per-deal rows + the renamable Dev row label
  extraOneTime: ExtraLine[];
  extraMonthly: ExtraLine[];
  devLabel?: string | null;
}
const emptyOption = (): ManualOption => ({
  config: 0, graphics: 0, dev: 0, licenseOneTime: 0, monthlyLicense: 0, retainBuyIn: 0,
});
const emptyManual = (): ManualPricing => ({
  perpetual: emptyOption(),
  twoYear: emptyOption(),
  fiveYear: emptyOption(),
  helpDesk: { gold: 0, platinum: 0, titanium: 0 },
  switchPilot: { offered: false, oneTimeConfig: 0, monthly: 0 },
  extraOneTime: [],
  extraMonthly: [],
  devLabel: null,
});
const emptyExtra = (): ExtraLine => ({
  label: '', perpetual: 0, twoYear: 0, fiveYear: 0,
});
const parseManual = (json?: string | null): ManualPricing | null => {
  if (!json) return null;
  try { return { ...emptyManual(), ...(JSON.parse(json) as ManualPricing) }; } catch { return null; }
};

export const RNWY_MANUAL_BADGE = 'PRICED EXTERNALLY';

// the little orange badge every engine surface shows while manual mode is on
export const RnwyManualBadge: React.FC = () => (
  <Badge colorScheme="orange" variant="solid">{RNWY_MANUAL_BADGE}</Badge>
);

const RnwyManualPricingPanel: React.FC = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const json = (values as { rnwyManualPricingJson?: string | null }).rnwyManualPricingJson;
  const manual = parseManual(json);
  const confirm = useConfirm();
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  // per-cell draft so typing decimals doesn't fight the parser
  const [draft, setDraft] = useState<Record<string, string>>({});

  const write = (m: ManualPricing) => form.change('rnwyManualPricingJson', JSON.stringify(m));

  const toggle = async (on: boolean) => {
    if (on) {
      const ok = await confirm(
        'Price this proposal externally? The engine will NOT price this deal — you enter every '
        + 'number by hand, and they flow unchanged into the work order, invoices, contract, and '
        + 'enrollment.',
        { title: 'Priced externally', confirmLabel: 'Price externally' },
      );
      if (ok) write(emptyManual());
    } else {
      const ok = await confirm(
        'Switch back to engine pricing? Your manually entered numbers will be WIPED and the '
        + 'engine re-prices this proposal from its venues × CFG.',
        { title: 'Back to engine pricing', confirmLabel: 'Wipe manual numbers' },
      );
      if (ok) form.change('rnwyManualPricingJson', null);
    }
  };

  const cellKey = (opt: string, field: string) => `${opt}.${field}`;
  const commit = (opt: keyof ManualPricing | 'helpDesk' | 'switchPilot', field: string, raw: string) => {
    if (!manual) return;
    const n = Number(raw);
    const v = Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : 0;
    const next: any = { ...manual };
    next[opt] = { ...(next[opt] as any), [field]: v };
    write(next);
    setDraft((d) => { const nd = { ...d }; delete nd[cellKey(opt as string, field)]; return nd; });
  };

  // item 959 — extras: label + per-option amounts, add/remove rows
  const setExtra = (list: 'extraOneTime' | 'extraMonthly', i: number, patch: Partial<ExtraLine>) => {
    if (!manual) return;
    const rows = [...(manual[list] ?? [])];
    rows[i] = { ...rows[i], ...patch };
    write({ ...manual, [list]: rows });
  };
  const addExtra = (list: 'extraOneTime' | 'extraMonthly') => {
    if (!manual) return;
    write({ ...manual, [list]: [...(manual[list] ?? []), emptyExtra()] });
  };
  const removeExtra = (list: 'extraOneTime' | 'extraMonthly', i: number) => {
    if (!manual) return;
    write({ ...manual, [list]: (manual[list] ?? []).filter((_, x) => x !== i) });
  };
  const extraCell = (list: 'extraOneTime' | 'extraMonthly', i: number, field: keyof ExtraLine, value: number) => (
    <Input
      size="sm"
      type="number"
      step="any"
      min={0}
      textAlign="right"
      value={draft[cellKey(`${list}.${i}`, field as string)] ?? (value ? String(value) : '')}
      placeholder="0"
      onChange={(e) => setDraft((d) => ({ ...d, [cellKey(`${list}.${i}`, field as string)]: e.target.value }))}
      onBlur={(e) => {
        const n = Number(e.target.value);
        setExtra(list, i, { [field]: Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : 0 } as Partial<ExtraLine>);
        setDraft((d) => { const nd = { ...d }; delete nd[cellKey(`${list}.${i}`, field as string)]; return nd; });
      }}
    />
  );
  const extraRows = (list: 'extraOneTime' | 'extraMonthly') => (manual?.[list] ?? []).map((row, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <Tr key={`${list}-${i}`}>
      <Td>
        <HStack spacing={1}>
          <IconButton
            aria-label="Remove row"
            icon={<FiTrash2 />}
            size="xs"
            variant="ghost"
            onClick={() => removeExtra(list, i)}
          />
          <Input
            size="sm"
            placeholder={list === 'extraOneTime' ? 'One-time item name (e.g. Enterprise)' : 'Monthly item name'}
            value={row.label ?? ''}
            onChange={(e) => setExtra(list, i, { label: e.target.value })}
          />
        </HStack>
      </Td>
      <Td>{extraCell(list, i, 'perpetual', row.perpetual)}</Td>
      <Td>{extraCell(list, i, 'twoYear', row.twoYear)}</Td>
      <Td>{extraCell(list, i, 'fiveYear', row.fiveYear)}</Td>
    </Tr>
  ));

  const numCell = (opt: string, field: string, value: number, disabled = false) => (
    <Input
      size="sm"
      type="number"
      step="any"
      min={0}
      textAlign="right"
      isDisabled={disabled}
      value={draft[cellKey(opt, field)] ?? (value ? String(value) : '')}
      placeholder="0"
      onChange={(e) => setDraft((d) => ({ ...d, [cellKey(opt, field)]: e.target.value }))}
      onBlur={(e) => commit(opt as any, field, e.target.value)}
    />
  );

  return (
    <Box mb={4}>
      {/* register the field so keepDirtyOnReinitialize protects in-flight edits (item 632 pattern) */}
      <Field name="rnwyManualPricingJson" subscription={{}}>{() => null}</Field>
      <HStack spacing={3} mb={2}>
        <Switch isChecked={!!manual} onChange={(e) => toggle(e.target.checked)} colorScheme="orange" />
        <Text fontSize="sm" fontWeight="semibold">Priced externally (manual)</Text>
        {manual && <RnwyManualBadge />}
      </HStack>
      <Text fontSize="xs" color={sub} mb={manual ? 3 : 0}>
        For deals priced outside the engine (e.g. by square footage). The engine is paused for this
        proposal and every number below is entered by hand — they flow unchanged into the work
        order, invoices, contract, and enrollment.
      </Text>
      {manual && (
        <>
          <Box overflowX="auto">
            <Table size="sm" variant="simple" maxW="760px">
              <Thead>
                <Tr>
                  <Th />
                  <Th isNumeric>Perpetual</Th>
                  <Th isNumeric>2-Year</Th>
                  <Th isNumeric>5-Year</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td fontSize="sm">Configuration &amp; deployment</Td>
                  <Td>{numCell('perpetual', 'config', manual.perpetual.config)}</Td>
                  <Td>{numCell('twoYear', 'config', manual.twoYear.config)}</Td>
                  <Td>{numCell('fiveYear', 'config', manual.fiveYear.config)}</Td>
                </Tr>
                <Tr>
                  <Td fontSize="sm">Graphic illustrations</Td>
                  <Td>{numCell('perpetual', 'graphics', manual.perpetual.graphics)}</Td>
                  <Td>{numCell('twoYear', 'graphics', manual.twoYear.graphics)}</Td>
                  <Td>{numCell('fiveYear', 'graphics', manual.fiveYear.graphics)}</Td>
                </Tr>
                <Tr>
                  {/* item 959 — the Dev row label is per-deal renamable ("call it whatever") */}
                  <Td>
                    <Input
                      size="sm"
                      variant="flushed"
                      fontSize="sm"
                      value={draft['devLabel.label'] ?? (manual.devLabel ?? 'Special feature development')}
                      onChange={(e) => setDraft((d) => ({ ...d, 'devLabel.label': e.target.value }))}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        write({ ...manual, devLabel: v && v !== 'Special feature development' ? v : null });
                        setDraft((d) => { const nd = { ...d }; delete nd['devLabel.label']; return nd; });
                      }}
                    />
                  </Td>
                  <Td>{numCell('perpetual', 'dev', manual.perpetual.dev)}</Td>
                  <Td>{numCell('twoYear', 'dev', manual.twoYear.dev)}</Td>
                  <Td>{numCell('fiveYear', 'dev', manual.fiveYear.dev)}</Td>
                </Tr>
                <Tr>
                  <Td fontSize="sm">One-time perpetual license</Td>
                  <Td>{numCell('perpetual', 'licenseOneTime', manual.perpetual.licenseOneTime)}</Td>
                  <Td><Text fontSize="xs" color={sub} textAlign="right">—</Text></Td>
                  <Td><Text fontSize="xs" color={sub} textAlign="right">—</Text></Td>
                </Tr>
                <Tr>
                  <Td fontSize="sm">Monthly license fee</Td>
                  <Td><Text fontSize="xs" color={sub} textAlign="right">—</Text></Td>
                  <Td>{numCell('twoYear', 'monthlyLicense', manual.twoYear.monthlyLicense)}</Td>
                  <Td>{numCell('fiveYear', 'monthlyLicense', manual.fiveYear.monthlyLicense)}</Td>
                </Tr>
                <Tr>
                  <Td fontSize="sm">Retain / Buy-in</Td>
                  <Td><Text fontSize="xs" color={sub} textAlign="right">—</Text></Td>
                  <Td>{numCell('twoYear', 'retainBuyIn', manual.twoYear.retainBuyIn)}</Td>
                  <Td>{numCell('fiveYear', 'retainBuyIn', manual.fiveYear.retainBuyIn)}</Td>
                </Tr>
                {/* item 959 — custom ONE-TIME rows (own WO/invoice lines, e.g. "Enterprise") */}
                {extraRows('extraOneTime')}
                {/* item 959 — custom MONTHLY rows (own subscription lines at enrollment) */}
                {extraRows('extraMonthly')}
                {/* item 961 (Chad: "no totaling screen") — live per-option totals */}
                {(() => {
                  const sumExtra = (list: ExtraLine[], k: 'perpetual' | 'twoYear' | 'fiveYear') => (list ?? [])
                    .reduce((t, r) => t + (Number(r[k]) || 0), 0);
                  const oneTime = (o: ManualOption, k: 'perpetual' | 'twoYear' | 'fiveYear') => o.config + o.graphics + o.dev
                    + o.licenseOneTime + o.retainBuyIn + sumExtra(manual.extraOneTime, k)
                    + (manual.switchPilot.offered ? manual.switchPilot.oneTimeConfig : 0);
                  const monthly = (o: ManualOption, k: 'perpetual' | 'twoYear' | 'fiveYear') => o.monthlyLicense
                    + sumExtra(manual.extraMonthly, k)
                    + (manual.switchPilot.offered ? manual.switchPilot.monthly : 0);
                  const fmt = (n: number) => (n > 0 ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');
                  return (
                    <>
                      <Tr>
                        <Td fontSize="sm" fontWeight="bold" borderTopWidth="2px">One-time total (incl. SwitchPilot)</Td>
                        <Td isNumeric fontWeight="bold" borderTopWidth="2px">{fmt(oneTime(manual.perpetual, 'perpetual'))}</Td>
                        <Td isNumeric fontWeight="bold" borderTopWidth="2px">{fmt(oneTime(manual.twoYear, 'twoYear'))}</Td>
                        <Td isNumeric fontWeight="bold" borderTopWidth="2px">{fmt(oneTime(manual.fiveYear, 'fiveYear'))}</Td>
                      </Tr>
                      <Tr>
                        <Td fontSize="sm" fontWeight="bold">Monthly total (excl. Help Desk)</Td>
                        <Td isNumeric fontWeight="bold">{fmt(monthly(manual.perpetual, 'perpetual'))}</Td>
                        <Td isNumeric fontWeight="bold">{fmt(monthly(manual.twoYear, 'twoYear'))}</Td>
                        <Td isNumeric fontWeight="bold" color="brandGreen.400">{fmt(monthly(manual.fiveYear, 'fiveYear'))}</Td>
                      </Tr>
                    </>
                  );
                })()}
              </Tbody>
            </Table>
          </Box>
          <HStack mt={2} spacing={3}>
            <Button size="xs" variant="outline" leftIcon={<FiPlus />} onClick={() => addExtra('extraOneTime')}>
              Add one-time item
            </Button>
            <Button size="xs" variant="outline" leftIcon={<FiPlus />} onClick={() => addExtra('extraMonthly')}>
              Add monthly item
            </Button>
          </HStack>
          <HStack mt={4} spacing={6} align="flex-start" wrap="wrap">
            <Box>
              <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase" opacity={0.7} mb={1}>
                Help Desk (monthly per tier)
              </Text>
              {(['gold', 'platinum', 'titanium'] as const).map((tier) => (
                <HStack key={tier} mb={1}>
                  <Text fontSize="sm" textTransform="capitalize" minW="72px">{tier}</Text>
                  <Box maxW="120px">{numCell('helpDesk', tier, manual.helpDesk[tier])}</Box>
                </HStack>
              ))}
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase" opacity={0.7} mb={1}>
                SwitchPilot
              </Text>
              <HStack mb={2}>
                <Switch
                  size="sm"
                  isChecked={manual.switchPilot.offered}
                  onChange={(e) => write({ ...manual, switchPilot: { ...manual.switchPilot, offered: e.target.checked } })}
                />
                <Text fontSize="sm">{manual.switchPilot.offered ? 'Included' : 'Not included'}</Text>
              </HStack>
              <HStack mb={1}>
                <Text fontSize="sm" minW="120px">One-time config</Text>
                <Box maxW="120px">{numCell('switchPilot', 'oneTimeConfig', manual.switchPilot.oneTimeConfig, !manual.switchPilot.offered)}</Box>
              </HStack>
              <HStack>
                <Text fontSize="sm" minW="120px">Monthly</Text>
                <Box maxW="120px">{numCell('switchPilot', 'monthly', manual.switchPilot.monthly, !manual.switchPilot.offered)}</Box>
              </HStack>
            </Box>
          </HStack>
          <Alert status="warning" borderRadius="md" fontSize="sm" mt={4}>
            <AlertIcon />
            Engine paused — the CFG rate card and venue quantities no longer affect PRICE on this
            proposal (venues still drive the scope of work and equipment lists). Save the proposal
            to lock these numbers into the deal chain.
          </Alert>
        </>
      )}
    </Box>
  );
};

export default RnwyManualPricingPanel;
